"use client";

import React, { useId } from "react";
import Link from "next/link";
import { ChevronRight, MapPin, Package, Search } from "lucide-react";
import { cn } from "utils/utils";
import { createCityUrl } from "modules/common/components/map/utils/formatCityName";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { useFacetSearchParamsContract } from "modules/common/hooks/useFacetSearchParamsContract";

export interface CityWithLocation {
  latitude: number;
  longitude: number;
  availableCount: number;
  wantedCount: number;
  storeCount: number;
  storeIds: number[];
  cityName?: string;
  city?: string;
  country?: string;
  isLoading: boolean;
}

interface CityListItemProps {
  city: CityWithLocation;
  cardName: string;
}

export const AVAILABILITY_CARD_CLASS =
  "border-base-content/10 bg-base-200/40 rounded-box block overflow-hidden border";

export const AVAILABILITY_HEADER_CLASS =
  "flex min-w-0 items-center gap-2 px-3 pt-2.5 pb-2";

/**
 * The two sides sit side by side so neither half reads as dead space.
 * `grid-cols-2` is load-bearing: daisyUI sizes `stats` columns to their
 * content, which makes the halves ragged once one side has an arrow chip and
 * the other doesn't.
 */
export const AVAILABILITY_SIDES_CLASS =
  "stats stats-horizontal border-base-content/10 grid w-full grid-cols-2 rounded-none border-t bg-transparent";

interface SideRowProps {
  /** Null when the count is zero, or when there is no city hub to link to. */
  href: string | null;
  side: "haves" | "wants";
  count: number;
  /** City or store name. Without it every link is announced as "Available 1". */
  place: string;
}

/**
 * One inventory side as a stat block. Both sides always render so the halves
 * stay the same width on every card; an empty side is muted and inert instead
 * of letting the present side stretch into a mostly-blank band.
 */
export function SideRow({ href, side, count, place }: SideRowProps) {
  const isHaves = side === "haves";
  const Icon = isHaves ? Package : Search;
  const isEmpty = count === 0;
  const tone = isHaves ? "text-warning" : "text-secondary";
  // Each side carries its own tint so the split reads at a glance; an empty
  // side keeps the tint at a fraction so the halves still pair up.
  const className = cn(
    "stat gap-0.5 px-3 py-2.5 transition-colors motion-reduce:transition-none",
    isHaves ? "bg-warning/10" : "bg-secondary/10",
    isEmpty && "bg-base-content/5",
    href && (isHaves ? "hover:bg-warning/20" : "hover:bg-secondary/20")
  );
  const content = (
    <>
      {/* Neutral, not `tone`: an amber chevron on the amber tint is 1.66:1 in
          the light theme, and it is the only click signal. */}
      {href && (
        <div className="stat-figure text-base-content/60">
          <ChevronRight size={20} />
        </div>
      )}
      <div className="stat-title text-base-content/70 flex items-center gap-1.5">
        <Icon
          size={16}
          className={cn("shrink-0", isEmpty ? "opacity-40" : tone)}
        />
        {isHaves ? "Available" : "Wanted"}
      </div>
      {/* The count stays base-content: `text-warning` on a light card is
          1.7:1. The side is coded by the icon and the arrow chip instead. */}
      <div
        className={cn(
          "stat-value text-3xl leading-tight",
          isEmpty && "text-base-content/50"
        )}
      >
        {count}
      </div>
    </>
  );
  return href ? (
    <Link
      href={href}
      className={className}
      aria-label={`${place} — ${count} ${isHaves ? "available" : "wanted"}`}
    >
      {content}
    </Link>
  ) : (
    <div className={className}>{content}</div>
  );
}

export function CityListItem({ city, cardName }: CityListItemProps) {
  const { serializeCityParams } = useFacetSearchParamsContract();
  const headingId = useId();

  // The location shows once; each inventory side is its own stat and its own
  // link, so a city that both has and wants the card never hides the wants
  // route.
  const hubLink =
    city.city && city.country ? createCityUrl(city.country, city.city) : null;
  const havesLink =
    hubLink && city.availableCount > 0
      ? serializeCityParams(`${hubLink}/haves`, { query: cardName })
      : null;
  const wantsLink =
    hubLink && city.wantedCount > 0
      ? serializeCityParams(`${hubLink}/wants`, { query: cardName })
      : null;

  // Loading state
  if (city.isLoading) {
    return <AvailabilityCardSkeleton />;
  }

  const storeLabel = `${city.storeCount} ${city.storeCount === 1 ? "store" : "stores"}`;
  const placeName =
    city.city ||
    city.cityName ||
    `${city.latitude.toFixed(2)}, ${city.longitude.toFixed(2)}`;
  return (
    <article className={AVAILABILITY_CARD_CLASS} aria-labelledby={headingId}>
      {/* City name and location */}
      <div className={AVAILABILITY_HEADER_CLASS}>
        <MapPin size={ICON_SIZE} className="text-base-content/50 shrink-0" />
        <div className="min-w-0 flex-1">
          <h3
            id={headingId}
            className="truncate text-sm font-semibold leading-tight"
          >
            {placeName}
          </h3>
          {city.country && (
            <p className="text-base-content/60 truncate text-xs">
              {city.country}
            </p>
          )}
        </div>
        <span className="badge badge-ghost badge-sm shrink-0 font-normal">
          {storeLabel}
        </span>
      </div>

      {/* Inventory sides */}
      <div className={AVAILABILITY_SIDES_CLASS}>
        <SideRow
          href={havesLink}
          side="haves"
          count={city.availableCount}
          place={placeName}
        />
        <SideRow
          href={wantsLink}
          side="wants"
          count={city.wantedCount}
          place={placeName}
        />
      </div>
    </article>
  );
}

/** Same two-part shape as a loaded card so the grid doesn't jump. */
export function AvailabilityCardSkeleton() {
  return (
    <div className={cn(AVAILABILITY_CARD_CLASS, "animate-pulse")}>
      <div className={AVAILABILITY_HEADER_CLASS}>
        <div className="skeleton h-5 w-5 shrink-0 rounded-full"></div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="skeleton h-3.5 w-24"></div>
          <div className="skeleton h-3 w-16"></div>
        </div>
        <div className="skeleton h-5 w-14 rounded-full"></div>
      </div>
      <div className={AVAILABILITY_SIDES_CLASS}>
        {[0, 1].map((i) => (
          <div key={i} className="flex flex-col gap-1.5 px-3 py-2.5">
            <div className="skeleton h-3 w-16"></div>
            <div className="skeleton h-6 w-8"></div>
          </div>
        ))}
      </div>
    </div>
  );
}
